import { inject, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { InjectableSuperclass } from '@sama/ng-core';
import { noop } from 'lodash';
import { combineLatest, lastValueFrom, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { MessagingService } from '../../../cross-team-shared/messaging/messaging.service';
import { BrowserControlService } from '../../../cross-team-shared/misc-services/browser-control.service';
import { MessageModalComponent } from '../../../cross-team-shared/misc-services/message-modal/message-modal.component';
import { EventTrackingService } from '../../../shared/event-tracking/event-tracking.service';
import { WorkspaceConfigService } from '../../../shared/misc-services/workspace-config.service';
import { WorkspaceLoadingService } from '../../../shared/misc-services/workspace-loading.service';
import { ExecutionQueueService } from '../../../shared/save-manager/execution-queue.service';
import { PeriodicSaveManagerService } from '../../../shared/save-manager/periodic-save-manager.service';
import { RedirectResponse } from '../../../team-apis/otterdam/redirect-response';
import { TaskExternal } from '../../../team-apis/otterdam/task';
import { TaskWorkspaceStore } from '../../h3ml/state/task-workspace-store';
import { FeatureFlagService } from '../../shared/misc-services/feature-flag.service';
import { TrainingTaskService } from '../../shared/misc-services/training-task.service';
import { TrainingTemplateService } from '../../shared/misc-services/training-template.service';
import { isRedirectResponse } from '../../shared/navigation/is-redirect-response';
import {
  isSubmissionErrors,
  SubmissionErrors,
} from '../../shared/utils/submission-errors-utils';
import { DraftSubmissionService } from '../submissions/draft-submission.service';
import { PersistenceManager } from '../submissions/persistence-manager';
import { RequiredAnnotationsService } from '../submissions/required-annotations.service';
import { SubmitTaskService } from '../submissions/submit-task.service';
import { TimerService } from '../timer-badge/timer.service';

type SubmitResultStatus = 'success' | 'benignError' | 'serverError' | undefined;

@Injectable()
export class SubmitTaskButtonService extends InjectableSuperclass {
  private persistenceManager = inject(PersistenceManager);
  private taskWorkspaceStore = inject(TaskWorkspaceStore);
  private browserControl = inject(BrowserControlService);
  private executionQueue = inject(ExecutionQueueService);
  private draftSubmissionService = inject(DraftSubmissionService);
  private dialogService = inject(MatDialog);
  private eventTrackingService = inject(EventTrackingService);
  private workspaceLoadingService = inject(WorkspaceLoadingService);
  private trainingTemplateService = inject(TrainingTemplateService);
  private trainingTaskService = inject(TrainingTaskService);
  private periodicSaveManager = inject(PeriodicSaveManagerService);
  private submitTaskService = inject(SubmitTaskService);
  private browserControlService = inject(BrowserControlService);
  private timerService = inject(TimerService);
  private requiredAnnotationsService = inject(RequiredAnnotationsService);
  private workspaceConfigService = inject(WorkspaceConfigService);
  private messagingService = inject(MessagingService);
  private featureFlagService = inject(FeatureFlagService);

  buildUnsubmittableReason$() {
    if (this.trainingTaskService.isInPracticeMode()) {
      return this.trainingTaskService.hasScoreCalculated$().pipe(
        switchMap((hasScoreCalculated) => {
          if (!hasScoreCalculated) {
            return of('Calculate score before submitting');
          }
          return this.getProductionUnsubmittableReason$();
        }),
      );
    } else {
      return this.getProductionUnsubmittableReason$();
    }
  }

  submit({ loadNextTask = true, resumeTimerOnError = true } = {}) {
    this.timerService.pause();
    if (this.taskWorkspaceStore.isUsingObjects()) {
      return this.submitForObjects(loadNextTask, resumeTimerOnError);
    } else {
      return this.submitObjectless();
    }
  }

  isNavbarSubmitButtonEnabled() {
    return (
      this.taskWorkspaceStore.isUsingObjects() &&
      (this.featureFlagService.hasFlag('new-submit-button') ||
        this.taskWorkspaceStore.isUsingGenAi())
    );
  }

  private getProductionUnsubmittableReason$() {
    if (this.taskWorkspaceStore.isUsingObjects()) {
      return combineLatest([
        this.requiredAnnotationsService.unsubmittableReason$(),
        this.submitTaskService.unsubmittableReason$(),
      ]).pipe(
        map(
          ([annotationReason, outputReason]) =>
            annotationReason || outputReason,
        ),
      );
    }
    return this.submitTaskService.unsubmittableReason$();
  }

  private async submitForObjects(
    loadNextTask: boolean,
    resumeTimerOnError: boolean,
  ) {
    if (this.workspaceLoadingService.isLoading$.value) {
      return;
    }

    this.trackSubmitEvent();

    this.workspaceLoadingService.isLoading$.next(true);
    let submitResultStatus: SubmitResultStatus;

    try {
      if (this.workspaceConfigService.templateId) {
        await this.trainingTemplateService.updateAnswers();
        return;
      }
      const result = await lastValueFrom(
        this.persistenceManager.submit(this.workspaceConfigService.qaGroupId!),
      );

      submitResultStatus = await this.handleSubmitResult(result, loadNextTask);
      return submitResultStatus;
    } finally {
      this.handleBenignError(submitResultStatus, resumeTimerOnError);
    }
  }

  private handleBenignError(
    submitResultStatus: SubmitResultStatus,
    resumeTimerOnError: boolean,
  ) {
    // allow user to try submitting again with benign or unrelated error
    if (submitResultStatus === 'benignError') {
      this.workspaceLoadingService.isLoading$.next(false);
      resumeTimerOnError && this.timerService.resume();
    }
  }

  private async handleSubmitResult(
    result: TaskExternal | RedirectResponse | SubmissionErrors,
    loadNextTask: boolean,
  ) {
    if (!isSubmissionErrors(result)) {
      await this.handleSubmitResolved(result, loadNextTask);
      return 'success';
    } else if (result.serverErrors.length) {
      this.handleFatalError();
      return 'serverError';
    }
    return 'benignError';
  }

  private async handleSubmitResolved(
    submitResult: TaskExternal | RedirectResponse,
    loadNextTask: boolean,
  ) {
    if (isRedirectResponse(submitResult)) {
      await this.redirectTo(submitResult.redirect_to, submitResult.message);
      return;
    }

    this.browserControlService.scrollToTop();
    loadNextTask && (await this.workspaceLoadingService.resetWorkspace());
  }

  private submitObjectless() {
    if (this.workspaceLoadingService.isLoading$.value) {
      return undefined;
    }

    this.trackSubmitEvent();

    this.workspaceLoadingService.isLoading$.next(true);

    return this.executionQueue.enqueue(async () => {
      let isSuccessOrServerError = false;
      try {
        if (this.workspaceConfigService.templateId) {
          await this.trainingTemplateService.updateAnswersObjectless();
        } else {
          this.periodicSaveManager.stop();
          const result = await this.submitTaskService.submit(
            this.workspaceConfigService.qaGroupId!,
          );
          isSuccessOrServerError =
            await this.handleSubmitResultObjectless(result);
        }
      } finally {
        // allow user to try submit again with benign or unrelated error
        if (!isSuccessOrServerError) {
          this.workspaceLoadingService.isLoading$.next(false);
          if (!this.workspaceConfigService.templateId) {
            this.periodicSaveManager.start();
          }
          this.timerService.resume();
        }
      }
    });
  }

  private async handleSubmitResultObjectless(
    result: TaskExternal | RedirectResponse | SubmissionErrors,
  ) {
    if (isSubmissionErrors(result)) {
      if (result.serverErrors.length) {
        this.handleFatalError();
        return true;
      }
    } else {
      await this.handleSubmitObjectless(result);
      return true;
    }
    return false;
  }

  private async handleSubmitObjectless(
    submitResult: TaskExternal | RedirectResponse,
  ) {
    this.draftSubmissionService.resetSavedAnswers();

    if (isRedirectResponse(submitResult)) {
      await this.redirectTo(submitResult.redirect_to, submitResult.message);
      return;
    }

    this.browserControlService.scrollToTop();
    await this.workspaceLoadingService.resetWorkspace();
  }

  private trackSubmitEvent() {
    this.eventTrackingService.track({
      id: 'submit_task',
      bucket: 'untracked',
      submission_duration: this.timerService.elapsed(),
      next_internal_id: this.taskWorkspaceStore.state().task.next_internal_id,
    });
  }

  private async redirectTo(location: string, message?: string) {
    if (message) {
      const modalRef = this.dialogService.open(MessageModalComponent, {
        data: { title: 'Notice', text: message },
        disableClose: true,
      });
      this.subscribeTo(modalRef.afterClosed(), () => {
        void this.browserControl.navigateTo(location);
      });
    } else {
      void this.browserControl.navigateTo(location);
    }
    return new Promise(noop); // never resolves
  }

  private handleFatalError() {
    this.taskWorkspaceStore('hasFatalError').set(true);
    this.messagingService.error(
      'Error while submitting. Please reload this page.',
    );
  }
}
